'use client';

import { Box, Alert } from '@mui/material';
import { DataTable, type DataTableColumn } from '@/shared/ui/data/DataTable';
import { Pagination } from '@/shared/ui/navigation/Pagination';
import { OrderTableSearch } from './OrderTableSearch';
import {
  useOrderSearch,
  useSearchMode,
  useOrderPagination,
  useLiveOrderSearch,
  LIVE_PARAM_KEYS,
} from '@/features/order';
import { useDataTableTheme } from '@/shared/ui/data/DataTable/theme/useDataTableTheme';
import {
  convertSimpleToAdvanced,
  convertAdvancedToSimple,
  defaultQuery,
  parseOrderSearchParams,
  serializeOrderSearchParams,
} from '../model/search';
import type { OrderView, OrderSearchView } from '@/entities/order';
import type { SimpleSearchQuery } from '@/shared/ui/search/SimpleSearch/types/search.types';
import type { SearchQuery } from '@/shared/ui/search/AdvancedSearch/types/query.types';
import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useServiceRegions } from '@/entities/service-region/model/useServiceRegions';
import { useServiceRegionDisplayName } from '@/entities/service-region/model/useServiceRegionDisplayName';
import { useOrderSearchQuery } from '@/features/order/search-orders/useOrderSearchQuery';
import { DataTableThemeTokens } from '@/shared/ui/data/DataTable/theme';
import { getStatusConfig } from '@/entities/order/ui';
import { isMoveDateImminent } from '@/entities/order';
import { TabContext } from '@/entities/tab/model/tab.types';
import { useUpdateTabParams } from '@/features/tab/tab-history';

interface IProps {
  initialQuery?: SimpleSearchQuery | SearchQuery;
  initialSearchMode?: 'simple' | 'advanced';
  pageSize?: number;
  onRowClick?: (order: OrderView) => void;
  onSearchChange?: (query: SimpleSearchQuery | SearchQuery) => void;
  /** 탭 컨텍스트 (탭별 독립적인 상태를 위해 사용) */
  tabContext?: TabContext;
}

function extractUniqueServiceRegionId(data: OrderSearchView | null): string[] {
  if (!data?.items) return [];
  const ids = data.items
    .map((order) => order.assignedServiceRegionId)
    .filter((id): id is string => !!id);
  return Array.from(new Set(ids));
}

export function OrderListTable({
  initialQuery,
  initialSearchMode = 'simple',
  pageSize = 20,
  onRowClick,
  onSearchChange,
  tabContext,
}: IProps) {
  const { tokens } = useDataTableTheme();
  const { updateTabParams } = useUpdateTabParams();

  // 탭 컨텍스트가 있으면 탭 params 사용, 없으면 URL 사용
  const tabParams = tabContext?.params;
  const handleTabParamsUpdate = (params: Record<string, unknown>) => {
    if (tabContext?.tabId) {
      updateTabParams(tabContext.tabId, params);
    }
  };

  const parsedSearch = useMemo(
    () => parseOrderSearchParams(tabParams as Record<string, unknown>),
    [tabParams]
  );
  const initialSearchModeFromParams = parsedSearch?.searchMode ?? initialSearchMode;
  const initialQueryFromParams = parsedSearch?.searchQuery ?? initialQuery;

  const { mode, setMode, isSimple, isAdvanced } = useSearchMode(initialSearchModeFromParams);
  const { currentQuery, setCurrentQuery, toOrderQuery } = useOrderSearchQuery({
    initialQuery: initialQueryFromParams ?? undefined,
    isSimple: isSimple,
  });
  const { data, loading, error, search } = useOrderSearch();

  const liveParamsFromTab = useMemo(() => {
    if (!tabParams) return undefined;
    const liveMode = tabParams[LIVE_PARAM_KEYS.liveMode];
    const liveSince = tabParams[LIVE_PARAM_KEYS.liveSince];
    const liveCursor = tabParams[LIVE_PARAM_KEYS.liveCursor];
    if (!liveMode || !liveSince) return undefined;
    return {
      [LIVE_PARAM_KEYS.liveMode]: String(liveMode),
      [LIVE_PARAM_KEYS.liveSince]: String(liveSince),
      [LIVE_PARAM_KEYS.liveCursor]:
        liveCursor != null && liveCursor !== '' ? String(liveCursor) : null,
    };
  }, [tabParams]);

  const handleLiveParamsChange = useCallback(
    (params: Record<string, unknown>) => {
      if (!tabContext?.tabId) return;
      handleTabParamsUpdate(params);
    },
    [tabContext?.tabId, handleTabParamsUpdate]
  );

  const liveOrderSearch = useLiveOrderSearch({
    initialParams: liveParamsFromTab,
    onParamsChange: handleLiveParamsChange,
  });

  const serviceRegionIds = useMemo(
    () => extractUniqueServiceRegionId(liveOrderSearch.isActive ? liveOrderSearch.data : data),
    [liveOrderSearch.isActive, liveOrderSearch.data, data]
  );
  const { serviceRegions } = useServiceRegions({ ids: serviceRegionIds });

  // 이전 serviceRegions를 유지하여 로딩 중에도 깜빡임 방지
  const previousServiceRegionsRef = useRef<typeof serviceRegions>([]);
  const stableServiceRegions = useMemo(() => {
    // 새로운 데이터가 있으면 사용하고, 없으면 이전 데이터 유지
    if (serviceRegions.length > 0) {
      previousServiceRegionsRef.current = serviceRegions;
      return serviceRegions;
    }
    // 이전 데이터가 있으면 유지
    return previousServiceRegionsRef.current.length > 0
      ? previousServiceRegionsRef.current
      : serviceRegions;
  }, [serviceRegions]);

  const { findServiceRegionDisplayNameById } = useServiceRegionDisplayName(stableServiceRegions);

  const executeSearch = useCallback(
    (event: { cursor?: string | null } | string | null) => {
      // Pagination의 onPageChange는 { cursor } 형태로 전달되지만,
      // 직접 호출 시 cursor만 전달될 수 있음
      const cursorValue: string | null =
        typeof event === 'object' && event !== null && 'cursor' in event
          ? (event.cursor ?? null)
          : typeof event === 'string'
            ? event
            : null;

      const cursor: string | undefined = cursorValue ?? undefined;

      if (currentQuery) search(toOrderQuery(currentQuery, cursor));
      else search(defaultQuery(pageSize, cursor));
    },
    [currentQuery, toOrderQuery, search, pageSize]
  );

  const { cursor, hasPreviousPage, hasNextPage, setCursorAndIndex } = useOrderPagination({
    data,
    onPageChange: (newCursor) => {
      executeSearch(newCursor);
    },
    initialPageSize: pageSize,
    tabParams,
    onTabParamsUpdate: handleTabParamsUpdate,
  });

  // 초기 로드
  useEffect(() => {
    executeSearch(cursor);
  }, []);

  // 실시간 모드 종료 시 일반 검색 복원
  const prevRealtimeRef = useRef(false);
  useEffect(() => {
    if (prevRealtimeRef.current && !liveOrderSearch.isActive) {
      setCursorAndIndex(null, 1);
      if (currentQuery) search(toOrderQuery(currentQuery, undefined));
      else search(defaultQuery(pageSize));
    }
    prevRealtimeRef.current = liveOrderSearch.isActive;
  }, [liveOrderSearch.isActive]);

  const handleRealtimeToggle = useCallback(() => {
    if (liveOrderSearch.isActive) {
      liveOrderSearch.stop();
    } else {
      liveOrderSearch.start();
    }
  }, [liveOrderSearch.isActive, liveOrderSearch.start, liveOrderSearch.stop]);

  const isRealtimeMode = liveOrderSearch.isActive;
  const displayData = isRealtimeMode ? liveOrderSearch.data : data;
  const displayLoading = isRealtimeMode ? liveOrderSearch.loading : loading;
  const displayError = isRealtimeMode ? liveOrderSearch.error : error;

  const handleSearch = async (searchQuery: SimpleSearchQuery | SearchQuery) => {
    // 검색 필터가 변경되면 커서와 페이지 인덱스를 초기화
    setCursorAndIndex(null, 1);

    setCurrentQuery(searchQuery);
    handleTabParamsUpdate(serializeOrderSearchParams(mode, searchQuery));
    // 검색 필터 변경 시 커서를 null로 명시적으로 전달하여 첫 페이지부터 검색
    const orderQuery = toOrderQuery(searchQuery, undefined);
    await search(orderQuery);
    onSearchChange?.(searchQuery);
  };

  const handleModeChange = (newMode: 'simple' | 'advanced') => {
    let nextQuery: SimpleSearchQuery | SearchQuery | null = currentQuery;
    if (currentQuery) {
      if (newMode === 'simple' && !isSimple) {
        const simpleQuery = convertAdvancedToSimple(currentQuery as SearchQuery) ?? {
          fieldName: undefined,
          value: '',
        };
        setCurrentQuery(simpleQuery);
        nextQuery = simpleQuery;
      } else if (newMode === 'advanced' && !isAdvanced) {
        const advancedQuery = convertSimpleToAdvanced(currentQuery as SimpleSearchQuery);
        setCurrentQuery(advancedQuery);
        nextQuery = advancedQuery;
      }
    }
    setMode(newMode);
    if (nextQuery) {
      handleTabParamsUpdate(serializeOrderSearchParams(newMode, nextQuery));
    }
  };

  const columns: DataTableColumn[] = useMemo(() => resolveDataTableColumns(tokens), [tokens]);
  const rows = useMemo(
    () => resolveDataTableRows(displayData, findServiceRegionDisplayNameById),
    [displayData, findServiceRegionDisplayNameById]
  );

  const handleRowClick = (row: any) => {
    if (row._order) onRowClick?.(row._order);
  };

  const initialSimpleQuery =
    isSimple && currentQuery ? (currentQuery as SimpleSearchQuery) : undefined;

  const initialAdvancedQuery =
    isAdvanced && currentQuery ? (currentQuery as SearchQuery) : undefined;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        <OrderTableSearch
          mode={mode}
          onModeChange={handleModeChange}
          onSearch={handleSearch}
          initialSimpleQuery={initialSimpleQuery}
          initialAdvancedQuery={initialAdvancedQuery}
          isRealtimeActive={isRealtimeMode}
          onRealtimeToggle={handleRealtimeToggle}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayError.message}
          </Alert>
        )}

        {!displayError && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {displayData && displayData.items.length > 0 ? (
              <>
                {isRealtimeMode && (
                  <Box
                    sx={{
                      py: 1,
                      px: 2,
                      mb: 1,
                      backgroundColor: 'action.selected',
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                    }}
                  >
                    실시간 조회 중 (버튼 클릭 시점 이후 접수된 주문, 5초 간격 갱신)
                  </Box>
                )}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <DataTable
                    columns={columns}
                    rows={rows}
                    onRowClick={handleRowClick}
                    stickyHeader
                  />
                </Box>

                {!isRealtimeMode && (
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      flexShrink: 0,
                      pb: 2,
                    }}
                  >
                    <Pagination
                      initialCursor={cursor}
                      initialPageSize={pageSize}
                      hasPreviousPage={hasPreviousPage}
                      hasNextPage={hasNextPage}
                      nextCursor={data?.nextCursor ?? null}
                      onPageChange={executeSearch}
                      tabContext={tabContext}
                    />
                  </Box>
                )}
              </>
            ) : !displayLoading && displayData && displayData.items.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>검색 결과가 없습니다.</Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function resolveDataTableColumns(tokens: DataTableThemeTokens): DataTableColumn[] {
  return [
    {
      id: 'misoOrderId',
      label: '주문번호',
      type: 'string',
      width: '120px',
      link: {
        to: (row) => `/workspace/orders/${row.id}?displayId=${row.misoOrderId}`,
      },
    },
    {
      id: 'status',
      label: '상태',
      type: 'label',
      width: '140px',
      variant: 'outlined',
      getColor: (value, row) => {
        const statusType = row?._order?.status?.type;
        if (!statusType) return 'default';
        return getStatusConfig(statusType).color;
      },
      getVariant: (value, row) => {
        const statusType = row?._order?.status?.type;
        if (!statusType) return 'outlined';
        return getStatusConfig(statusType).variant;
      },
    },
    {
      id: 'serviceRegion',
      label: '서비스 구역',
      type: 'string',
      width: '120px',
    },
    {
      id: 'orderedAt',
      label: '주문일시',
      type: 'datetime',
      width: '200px',
      format: 'YYYY. MM. DD. A hh:mm',
    },
    {
      id: 'moveDate',
      label: '이사일',
      type: 'date',
      width: '150px',
      format: 'YYYY MM DD',
      conditionalFormatting: {
        condition: (value) => isMoveDateImminent(value),
        sx: {
          backgroundColor: tokens.conditionalFormatting.warning.background,
          color: tokens.conditionalFormatting.warning.color,
          padding: tokens.conditionalFormatting.warning.padding,
          borderRadius: tokens.conditionalFormatting.warning.borderRadius,
          display: 'inline-block',
        },
      },
    },
    {
      id: 'departure',
      label: '출발지',
      type: 'string',
      width: '400px',
    },
    {
      id: 'arrival',
      label: '도착지',
      type: 'string',
      width: '400px',
    },
  ];
}

function resolveDataTableRows(
  data: OrderSearchView | null,
  findServiceRegionDisplayNameById: (id: string | undefined) => string
) {
  if (!data?.items) return [];
  return data.items.map((order) => {
    const moveDate = order.moving.moveDate; // string (YYYY-MM-DD)
    const serviceRegionName = findServiceRegionDisplayNameById(
      order.assignedServiceRegionId ?? undefined
    );

    return {
      id: order.id,
      misoOrderId: order.misoOrderId,
      moveDate,
      departure: order.moving.departure.location,
      arrival: order.moving.arrival?.location || '-',
      status: getStatusConfig(order.status.type).label,
      orderedAt: order.orderedAt, // string (ISO 8601)
      serviceRegion: serviceRegionName,
      _order: order, // 원본 데이터 보관
    };
  });
}
